import type {
  ContractId,
  MissionObjective,
  MissionView,
} from "@frontier/content/contracts";
import type { PublicEventView } from "@frontier/content/events";
import { contractComplete } from "@frontier/content/progression";
import { stageLabels } from "./flight-labels";
import type { TutorialGuidance } from "./onboarding-ui";
import "./onboarding.css";
import { pressHandlers } from "./press-handlers";
import "./mission-roster.css";
import { MissionRequirements } from "./MissionRequirements";

const contractLabels: Record<ContractId, string> = {
  intro: "ОБУЧЕНИЕ", hunt: "ОХОТА", salvage: "ОБЛОМКИ", signal: "СИГНАЛ", escort: "КОНВОЙ",
};

export function MissionReadout({
  mission,
  missions,
  onFocusContract,
  guidance,
  event,
  eventObjective,
  contractTracked = !eventObjective,
}: {
  event?: PublicEventView | null;
  eventObjective?: MissionObjective | null;
  mission: MissionView | null;
  missions: readonly MissionView[];
  onFocusContract: (id: ContractId) => void;
  guidance: TutorialGuidance | null;
  contractTracked?: boolean;
}) {
  const roster =
    missions.length > 0 ? (
      <nav className="mission-roster" aria-label="Контракты вылета">
        {missions.map((entry) => (
          <button
            key={entry.orderId}
            aria-pressed={contractTracked && entry.orderId === mission?.orderId}
            aria-label={`${entry.title}: ${entry.progress}/${entry.goal}. Показать маршрут`}
            {...pressHandlers(() => onFocusContract(entry.contractId))}
          >
            <span>
              {entry.title}
              <small>{contractLabels[entry.contractId]} · {contractTracked && entry.orderId === mission?.orderId ? "ОТСЛЕЖИВАЕТСЯ" : "ПОКАЗАТЬ МАРШРУТ"}</small>
            </span>
            <b>
              {entry.outcome === "success" ||
              (entry.outcome === "active" && contractComplete(entry))
                ? "✓"
                : `${entry.progress}/${entry.goal}`}
            </b>
          </button>
        ))}
      </nav>
    ) : null;
  if (event && eventObjective)
    return (
      <section
        className="mission public-event-mission"
        aria-label="Отслеживаемое событие"
        data-event-id={event.id}
      >
        <span className="mission-symbol">⚑</span>
        <div className="mission-copy">
          {roster}
          <span className="eyebrow">СОБЫТИЕ / {event.title}</span>
          <div className="mission-heading">
            <b>{eventObjective.name}</b>
            <span>
              {Math.round(event.progress)}/{event.goal}
            </span>
          </div>
          <MissionRequirements mission={mission} />
        </div>
      </section>
    );
  if (!mission) return null;
  return (
    <section
      className={`mission ${mission.rescue === "active" ? "distress" : ""}`}
      aria-label="Текущий этап контракта"
      data-stage={mission.stage}
      data-progress={mission.progress}
      data-outcome={mission.outcome}
    >
      <span className="mission-symbol">
        {mission.stage === "return"
          ? "↶"
          : mission.stage === "settled"
            ? "✓"
            : "◇"}
      </span>
      <div className="mission-copy">
        {roster}
        <span className="eyebrow">
          {`${mission.contractId === "intro" ? "ПЕРВЫЙ ВЫЛЕТ" : contractComplete(mission) ? "ГОТОВ К СДАЧЕ" : stageLabels[mission.stage]} / ${mission.title}`}
        </span>
        <div className="mission-heading">
          <b>{mission.objective.name}</b>
          <span>
            {mission.stage === "settled"
              ? "✓"
              : `${mission.progress}/${mission.goal}`}
          </span>
        </div>
        <MissionRequirements mission={mission} />
        {guidance && (
          <small
            className={`tutorial-guidance ${guidance.alert ? "alert" : ""}`}
            role="status"
          >
            {guidance.message}
          </small>
        )}
        {mission.weather === "ion-storm" && (
          <small className="mission-condition">
            ИОННАЯ БУРЯ · РЕГЕНЕРАЦИЯ ЩИТА СНИЖЕНА
          </small>
        )}
        {mission.rescue === "active" && !mission.terms.requireRescue && (
          <small className="mission-opportunity">
            СИГНАЛ БЕДСТВИЯ · МОЖНО ИЗМЕНИТЬ МАРШРУТ
          </small>
        )}
      </div>
    </section>
  );
}
